// Offline-Speicher für alle Lieder.
// Die Liederliste und alle Liedtexte werden im localStorage abgelegt und bei
// bestehender Internetverbindung mit der Datenbank abgeglichen.

const db_api = '/api.php';
const edit_api = '/edit_add_api.php';
const bulk_api = `${edit_api}?action=all`;
const STORAGE_KEY = 'offline_lieder';
const SCHEMA = 5; // erhöhen, wenn sich das Format der gespeicherten Lieder ändert (erzwingt neuen Abgleich)
const PARALLEL_REQUESTS = 6;
const MIN_SYNC_INTERVAL = 30 * 1000; // der Abgleich ist winzig, wenn sich nichts geändert hat

// Wolgalieder bleiben in der Datenbank, die App kennt nur die Jugendlieder.
const ohneWolga = (lieder) => lieder.filter((lied) => lied.quelle !== 'wolga');

let cache = loadCache();
let syncPromise = null;
let lastSync = 0;
const listeners = new Set();

function loadCache() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (stored && stored.schema === SCHEMA && Array.isArray(stored.list) && stored.details) {
      // Auch bereits gespeicherte Wolgalieder aussortieren (Speicher aus älteren Versionen).
      const list = ohneWolga(stored.list);
      const details = {};
      list.forEach((lied) => {
        const key = String(lied.id);
        if (stored.details[key]) details[key] = stored.details[key];
      });
      return { ...stored, list, details };
    }
  } catch (err) { }
  return { list: [], details: {}, version: '', schema: SCHEMA };
}

function saveCache() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.error('Lieder konnten nicht offline gespeichert werden', err);
  }
}

function notify() {
  listeners.forEach((listener) => listener(cache.list));
}

async function fetchJson(url) {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json();
}

function toRecord(data) {
  return Array.isArray(data) ? data[0] : data;
}

function normalizeRecord(record) {
  if (!record) return record;
  return {
    ...record,
    bloecke: (record.bloecke || []).map((b) => ({ ...b, block_typ: String(b.block_typ).toLowerCase() }))
  };
}

// Einzelnes Lied inkl. Blöcken und Audiodatei (audio_url) vom Server holen.
async function fetchSong(id) {
  const key = encodeURIComponent(id);
  try {
    return normalizeRecord(toRecord(await fetchJson(`${edit_api}?action=get&id=${key}`)));
  } catch (err) {
    return normalizeRecord(toRecord(await fetchJson(`${db_api}?id=${key}`)));
  }
}

// Schneller Weg: alle Lieder in einer Anfrage. Ist die gespeicherte Version
// aktuell, kommt nur eine winzige Antwort zurück.
async function fetchBulk() {
  const url = cache.version && cache.list.length
    ? `${bulk_api}&version=${encodeURIComponent(cache.version)}`
    : bulk_api;
  const data = await fetchJson(url);
  if (data && data.unchanged) return null;
  if (!data || !Array.isArray(data.lieder)) throw new Error('Ungültige Antwort');

  const details = {};
  const list = ohneWolga(data.lieder).map(({ bloecke, ...lied }) => {
    details[String(lied.id)] = normalizeRecord({ ...lied, bloecke });
    return lied;
  });
  return { list, details, version: data.version, schema: SCHEMA };
}

// Ausweichweg (z. B. falls der Bulk-Endpunkt nicht erreichbar ist): ein Lied pro Anfrage.
async function fetchPerSong() {
  let list;
  try {
    list = await fetchJson(`${edit_api}?action=list`); // mit Quelle und Nummer
  } catch (err) {
    list = await fetchJson(db_api);
  }
  if (!Array.isArray(list)) throw new Error('Ungültige Liederliste');
  list = ohneWolga(list);

  const details = {};
  const ids = list.map((lied) => String(lied.id));
  let index = 0;

  async function worker() {
    while (index < ids.length) {
      const id = ids[index++];
      try {
        const record = await fetchSong(id);
        if (record) details[id] = record;
      } catch (err) {
        if (cache.details[id]) details[id] = cache.details[id];
      }
    }
  }

  await Promise.all(Array.from({ length: PARALLEL_REQUESTS }, worker));
  return { list, details, version: '', schema: SCHEMA };
}

async function doSync() {
  let next;
  try {
    next = await fetchBulk();
    if (!next) {
      lastSync = Date.now();
      return;
    }
  } catch (err) {
    next = await fetchPerSong();
  }

  if (JSON.stringify(next) !== JSON.stringify(cache)) {
    cache = next;
    saveCache();
    notify();
  }
  lastSync = Date.now();
}

// Gleicht die lokalen Daten mit der Datenbank ab (nur wenn online).
export function syncLieder() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return Promise.reject(new Error('offline'));
  }
  if (!syncPromise) {
    // Abgleich ersetzt den Speicher komplett: neue Lieder kommen dazu, auf dem Server gelöschte verschwinden.
    syncPromise = doSync().finally(() => {
      syncPromise = null;
    });
  }
  return syncPromise;
}

// Startet den automatischen Abgleich: beim App-Start, sobald wieder eine
// Verbindung besteht und wenn die App wieder in den Vordergrund kommt.
export function startOfflineSync() {
  const trySync = () => syncLieder().catch(() => { });
  trySync();
  window.addEventListener('online', trySync);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - lastSync > MIN_SYNC_INTERVAL) {
      trySync();
    }
  });
}

// Liefert die Liederliste – sofort aus dem Speicher oder, falls noch nichts
// gespeichert ist, nach dem ersten Abgleich.
export function loadLiederListe() {
  if (cache.list.length) return Promise.resolve(cache.list);
  return syncLieder().then(() => cache.list);
}

// Liefert ein einzelnes Lied inkl. Blöcken.
export async function loadLied(id) {
  const key = String(id);
  if (cache.details[key]) return cache.details[key];

  const record = await fetchSong(key);
  if (record) {
    cache.details[key] = record;
    saveCache();
  }
  return record;
}

// Wird aufgerufen, wenn sich die Liederliste nach einem Abgleich geändert hat.
export function subscribeLieder(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Liefert alle Lieder inkl. Blöcken (für die Volltextsuche).
export async function loadAlleLieder() {
  const list = await loadLiederListe();
  return Promise.all(list.map((lied) => loadLied(lied.id).then((record) => record || lied).catch(() => lied)));
}
